import { useId } from "react";

import { Input, Label, StatusMessage, cn } from "@ceedero/ui";

import type { ComponentProps } from "react";

interface FormFieldProps extends Omit<ComponentProps<typeof Input>, "id"> {
  /** Already localized. This component owns no product language. */
  errorMessage?: string | undefined;
  hint?: string | undefined;
  /**
   * Kept as the accessible name but not drawn, for a field whose section
   * heading already names it — "Display name" over a box labelled "Display
   * name" is the same word twice, and the second one is not a label so much as
   * an echo. The association survives, so a screen reader still reads a name.
   */
  isLabelHidden?: boolean | undefined;
  label: string;
}

/**
 * A labelled text field with the message that appears when it is wrong.
 *
 * Its whole job is the wiring, which is the half that is silently dropped when
 * each form writes its own: the label has to point at the input, the input has
 * to be marked invalid, and the message has to be named as the input's
 * description or a screen reader reaches the field and is told nothing about
 * why it was rejected. Six forms collecting a credential is six chances to
 * forget one of the three.
 *
 * The ids are generated here rather than asked of the caller, because a caller
 * that has to invent an id is a caller that can hand the same one to two
 * fields.
 */
export const FormField = ({
  errorMessage,
  hint,
  isLabelHidden = false,
  label,
  ...inputProps
}: FormFieldProps) => {
  const fieldId = useId();
  const errorId = useId();
  const hintId = useId();
  const describedBy = [
    hint === undefined ? undefined : hintId,
    errorMessage === undefined ? undefined : errorId,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={fieldId} className={cn(isLabelHidden && "sr-only")}>
        {label}
      </Label>
      <Input
        id={fieldId}
        aria-invalid={errorMessage === undefined ? undefined : true}
        aria-describedby={describedBy.length === 0 ? undefined : describedBy}
        {...inputProps}
      />
      {hint !== undefined && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {errorMessage !== undefined && (
        <StatusMessage id={errorId} tone="error">
          {errorMessage}
        </StatusMessage>
      )}
    </div>
  );
};
